import React, { useEffect, useState } from 'react';
import { getInbox, getConversation, sendMessage } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MessageSquare, Send, Loader2, User } from 'lucide-react';

export default function MessagesScreen(): React.JSX.Element {
  const toast = useToast();
  const { user } = useAuth();
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [conversation, setConversation] = useState<any[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadInbox();
  }, []);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const res = await getInbox();
      setMessages(res.messages || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load messages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (otherUser: any) => {
    setSelectedUser(otherUser);
    try {
      const res = await getConversation(otherUser.id);
      setConversation(res.messages || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load conversation', 'error');
    }
  };

  const handleSend = async () => {
    if (!replyText.trim() || !selectedUser) return;
    setSending(true);
    try {
      await sendMessage({
        receiver_id: selectedUser.id,
        message: replyText,
      });
      setReplyText('');
      loadConversation(selectedUser);
      loadInbox();
    } catch (err: any) {
      toast.show(err.message || 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="w-7 h-7 text-amber-500" />
        <h1 className="text-2xl font-bold text-slate-900">Messages</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-1 max-h-[600px] overflow-y-auto">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Inbox</h2>
          {messages.length === 0 ? (
            <p className="text-sm text-slate-400">No messages yet.</p>
          ) : (
            <div className="space-y-2">
              {messages.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => loadConversation({ id: msg.sender_id, name: msg.sender_name, avatar: msg.sender_avatar })}
                  className={`w-full text-left p-3 rounded-xl transition-colors ${
                    selectedUser?.id === msg.sender_id ? 'bg-amber-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {msg.sender_avatar ? (
                      <img src={msg.sender_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{msg.sender_name}</p>
                      <p className="text-xs text-slate-500 truncate">{msg.subject || msg.message}</p>
                    </div>
                    {!msg.is_read && <span className="w-2 h-2 bg-amber-500 rounded-full" />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="md:col-span-2 flex flex-col max-h-[600px]">
          {selectedUser ? (
            <>
              <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">
                  {selectedUser.name?.charAt(0)?.toUpperCase()}
                </div>
                <span className="font-medium text-slate-900">{selectedUser.name}</span>
              </div>
              <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                {conversation.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                        msg.sender_id === user?.id
                          ? 'bg-amber-500 text-white rounded-br-md'
                          : 'bg-slate-100 text-slate-800 rounded-bl-md'
                      }`}
                    >
                      {msg.message}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-3 border-t border-slate-100">
                <input
                  type="text"
                  className="flex-1 px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  placeholder="Type a message..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                />
                <Button onClick={handleSend} disabled={sending || !replyText.trim()}>
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <p>Select a conversation to view messages</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
